import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Link from "@cloudscape-design/components/link"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import TextFilter from "@cloudscape-design/components/text-filter"
import { useState, useEffect, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import type { DTARecord } from "../../services/dtaApi"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import Pagination from "../../components/Pagination/Pagination"
import { getDTADisplayStatus, stageBadgeColor } from "../../components/shared/dtaDisplay"
import { useAuth } from "../../contexts/AuthContext"
import { useDashboardStats } from "../../hooks/queries/useDashboard"
import { useAllDtasList } from "../../hooks/queries/useDtas"
import { usePageParam } from "../../hooks/usePageParam"

const ITEMS_PER_PAGE = 20

const STATUS_OPTIONS = [
  { label: "All Status", value: "all" },
  { label: "Draft", value: "DRAFT" },
  { label: "Review", value: "REVIEW" },
  { label: "Approved", value: "APPROVED" },
  { label: "Active", value: "ACTIVE" },
]

const AllDTAsView = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const daeId = user?.principalId

  const { data: dashboardInfo } = useDashboardStats(daeId)

  // Filters and search
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTermRaw] = useState("")
  // Status filter is URL-backed so links (e.g. the dashboard "Review pending DTAs" shortcut) can
  // deep-link to a pre-filtered view. An unrecognised value falls back to "all".
  const rawStatus = searchParams.get("status") ?? "all"
  const statusFilter = STATUS_OPTIONS.some((o) => o.value === rawStatus) ? rawStatus : "all"
  const [currentPage, setCurrentPage] = usePageParam()
  const [sorting, setSorting] = useState<TableProps.SortingState<DTARecord>>({
    sortingColumn: { sortingField: "last_modified_at" },
    isDescending: true,
  })

  const setSearchTerm = (next: string) => {
    setSearchTermRaw(next)
    setCurrentPage(1)
  }

  const setStatusFilter = (next: string) => {
    // Set status and reset to page 1 in a single navigation. Two separate setSearchParams calls
    // would race — the second reads pre-update params and clobbers the first.
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next === "all") params.delete("status")
        else params.set("status", next)
        params.delete("page") // usePageParam treats absent "page" as page 1
        return params
      },
      { replace: true },
    )
  }

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isRefetching,
    refetch,
  } = useAllDtasList(user?.principalId, ITEMS_PER_PAGE)

  // Auto-fetch remaining pages in the background. `data` is a dependency (not just the two flags)
  // because a page that resolves before React commits the in-flight render leaves both flags
  // unchanged - without it the drain stalls after the second page.
  useEffect(() => {
    if (data && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [data, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Flatten all fetched pages into a single collection
  const allDTAs = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])

  // Stats derived from the full collection
  const stats = useMemo(() => {
    const total = dashboardInfo?.dtas.total ?? 0
    const approved = dashboardInfo?.dtas.active ?? 0
    return {
      totalDTAs: total,
      approvedDTAs: approved,
    }
  }, [dashboardInfo])

  // Filter and sort across the ENTIRE collection. Sorting is driven by the Cloudscape Table.
  const filteredDTAs = useMemo(() => {
    const { sortingColumn, isDescending } = sorting
    const comparator = sortingColumn.sortingComparator
    const field = sortingColumn.sortingField as keyof DTARecord | undefined
    const direction = isDescending ? -1 : 1
    return allDTAs
      .filter((dta) => {
        const matchesSearch =
          searchTerm === "" ||
          dta.study_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dta.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dta.domains?.some((d) => d.toLowerCase().includes(searchTerm.toLowerCase()))
        const displayStatus = getDTADisplayStatus(dta)
        const matchesStatus = statusFilter === "all" || displayStatus === statusFilter
        return matchesSearch && matchesStatus
      })
      .sort((a, b) => {
        let comparison: number
        if (comparator) {
          comparison = comparator(a, b)
        } else if (!field) {
          comparison = 0
        } else {
          const aVal = String(a[field] ?? "")
          const bVal = String(b[field] ?? "")
          comparison = aVal.localeCompare(bVal)
        }
        return comparison * direction
      })
  }, [allDTAs, searchTerm, statusFilter, sorting])

  // Client-side page slice over the filtered results
  // Use server-side total for page count (instant), fall back to loaded collection size
  const isFiltering = statusFilter !== "all" || searchTerm !== ""
  const totalForPagination = isFiltering
    ? filteredDTAs.length
    : (dashboardInfo?.dtas.total ?? filteredDTAs.length)
  const pageCount = Math.max(1, Math.ceil(totalForPagination / ITEMS_PER_PAGE))
  const paginatedDTAs = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredDTAs.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredDTAs, currentPage])

  // A deep link (or a page the user was on before filtering) can point past the end. Only clamp once
  // every page has loaded, otherwise the deep link is lost while the background fetch is still going.
  const fullyLoaded = !isLoading && !hasNextPage && !isFetchingNextPage
  useEffect(() => {
    if (fullyLoaded && currentPage > pageCount) setCurrentPage(pageCount)
  }, [fullyLoaded, currentPage, pageCount, setCurrentPage])

  const handleRefresh = () => {
    setCurrentPage(1)
    void refetch()
  }

  const columnDefinitions: TableProps.ColumnDefinition<DTARecord>[] = useMemo(
    () => [
      {
        id: "study_id",
        header: "Study",
        sortingField: "study_id",
        cell: (dta) => (
          <Link
            href={`/dtas/${dta.dta_id}`}
            onFollow={(e) => {
              e.preventDefault()
              navigate(`/dtas/${dta.dta_id}`)
            }}
            variant="primary"
          >
            {dta.study_id}
          </Link>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortingField: "workflow_stage",
        cell: (dta) => (
          <Badge color={stageBadgeColor(dta.workflow_stage)}>{getDTADisplayStatus(dta)}</Badge>
        ),
      },
      {
        id: "vendor_name",
        header: "Vendor",
        sortingField: "vendor_name",
        cell: (dta) => dta.vendor_name || "N/A",
      },
      {
        id: "domains",
        header: "Domains",
        sortingComparator: (a, b) => {
          const aVal = [...(a.domains ?? [])].sort((x, y) => x.localeCompare(y))[0] ?? ""
          const bVal = [...(b.domains ?? [])].sort((x, y) => x.localeCompare(y))[0] ?? ""
          return aVal.localeCompare(bVal)
        },
        cell: (dta) => (
          <SpaceBetween direction="horizontal" size="xxs">
            {dta.domains?.slice(0, 2).map((domain) => (
              <Badge key={domain} color="blue">
                {domain}
              </Badge>
            ))}
            {(dta.domains?.length || 0) > 2 && (
              <Badge color="grey">+{(dta.domains?.length || 0) - 2}</Badge>
            )}
          </SpaceBetween>
        ),
      },
      {
        id: "version",
        header: "Version",
        cell: (dta) => `v${dta.version}`,
      },
      {
        id: "last_modified_at",
        header: "Last Modified",
        sortingField: "last_modified_at",
        sortingComparator: (a, b) =>
          new Date(a.last_modified_at).getTime() - new Date(b.last_modified_at).getTime(),
        cell: (dta) => new Date(dta.last_modified_at).toLocaleString(),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (dta) => (
          <Link
            href={`/dtas/${dta.dta_id}`}
            onFollow={(e) => {
              e.preventDefault()
              navigate(`/dtas/${dta.dta_id}`)
            }}
          >
            View Details
          </Link>
        ),
      },
    ],
    [navigate],
  )

  return (
    <SpaceBetween size="l">
      <Breadcrumb items={[{ label: "Data Transfer Agreements" }]} />

      <Header
        variant="h1"
        description="Browse and manage all Data Transfer Agreements across studies"
      >
        Data Transfer Agreements
      </Header>

      {error && (
        <Box color="text-status-error">
          <SpaceBetween size="xxs">
            <Box fontWeight="bold">Unable to Load DTAs</Box>
            <Box>{error.message}</Box>
          </SpaceBetween>
        </Box>
      )}

      {/* Stat tiles span the full row, split evenly via a 6/6 Grid. */}
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
        <Container>
          <SpaceBetween direction="horizontal" size="s">
            <Box color="text-status-info">
              <Icon name="file" size="medium" />
            </Box>
            <Box>
              <Box color="text-body-secondary" fontSize="body-s">
                Total
              </Box>
              <Box fontSize="heading-l" fontWeight="bold">
                {!dashboardInfo ? <Spinner /> : stats.totalDTAs}
              </Box>
            </Box>
          </SpaceBetween>
        </Container>
        <Container>
          <SpaceBetween direction="horizontal" size="s">
            <Box color="text-status-success">
              <Icon name="status-positive" size="medium" />
            </Box>
            <Box>
              <Box color="text-body-secondary" fontSize="body-s">
                Approved
              </Box>
              <Box fontSize="heading-l" fontWeight="bold">
                {!dashboardInfo ? <Spinner /> : stats.approvedDTAs}
              </Box>
            </Box>
          </SpaceBetween>
        </Container>
      </Grid>

      <Container>
        <Table
          variant="embedded"
          contentDensity="comfortable"
          columnDefinitions={columnDefinitions}
          items={paginatedDTAs}
          loading={isLoading || (paginatedDTAs.length === 0 && (hasNextPage || isFetchingNextPage))}
          loadingText="Loading DTAs..."
          trackBy="dta_id"
          sortingColumn={sorting.sortingColumn}
          sortingDescending={sorting.isDescending}
          onSortingChange={({ detail }) => setSorting(detail)}
          header={
            <Header
              counter={`(${stats.totalDTAs})`}
              actions={
                <Button iconName="refresh" onClick={handleRefresh} disabled={isRefetching}>
                  Refresh
                </Button>
              }
            >
              Data Transfer Agreements
            </Header>
          }
          filter={
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <div style={{ flexGrow: 1, maxWidth: 480 }}>
                <TextFilter
                  filteringText={searchTerm}
                  filteringPlaceholder="Search by Study ID, Vendor, or Domain..."
                  filteringAriaLabel="Search DTAs"
                  onChange={({ detail }) => setSearchTerm(detail.filteringText)}
                />
              </div>
              <div style={{ minWidth: 180 }}>
                <Select
                  selectedOption={
                    STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_OPTIONS[0]
                  }
                  options={STATUS_OPTIONS}
                  onChange={({ detail }) => setStatusFilter(detail.selectedOption.value ?? "all")}
                  ariaLabel="Filter by status"
                />
              </div>
            </div>
          }
          empty={
            <Box textAlign="center" color="inherit">
              {error ? "Unable to load DTAs" : "No DTAs found"}
            </Box>
          }
          pagination={
            <Pagination
              currentPage={currentPage}
              pageCount={pageCount}
              loading={isLoading}
              onNext={() => setCurrentPage(currentPage + 1)}
              onPrevious={() => setCurrentPage(currentPage - 1)}
              onPageChange={setCurrentPage}
            />
          }
        />
      </Container>
    </SpaceBetween>
  )
}

export default AllDTAsView
